<template>
  <div class="measurement-tool">
    <!-- Controls -->
    <div class="controls-row">
      <div class="control-group">
        <div class="checkbox-group">
          <label for="showGuidelines">Show Help Text</label>
          <input type="checkbox" id="showGuidelines" v-model="showLeuteinGuidelines" @change="draw" />
        </div>
      </div>
      
      <div class="button-group">
        <button @click="rotateCCW" class="control-btn">
          <i class="fa-solid fa-rotate-left"></i>
          <span class="btn-text">Rotate CCW</span>
        </button>
        <button @click="rotateCW" class="control-btn">
          <i class="fa-solid fa-arrow-rotate-right"></i>
          <span class="btn-text">Rotate CW</span>
        </button>
        <button @click="resetTool" class="control-btn">
          <i class="fa-solid fa-arrows-rotate"></i>
          <span class="btn-text">Reset</span>
        </button>
        <!-- <button @click="adjustPointsToLeuteinMethod" class="control-btn">
          <i class="fa-solid fa-ruler-combined"></i>
          <span class="btn-text">Adjust to Leutein</span>
        </button> -->
      </div>
    </div>

    <!-- File input -->
    <div class="file-input">
      <label for="imageUpload" class="file-upload-btn">
        <i class="fa-solid fa-upload"></i>
        <span>Upload Image</span>
      </label>
      <input type="file" id="imageUpload" @change="handleImageUpload" accept=".jpg,.jpeg,.png" />
    </div>

    <!-- Top right display of curvature angle -->
    <div id="angle-bottom-right" ref="angleTopRight">Curvature: 0°</div>

    <!-- Canvas container with responsive sizing -->
    <div class="canvas-container" ref="canvasContainer">
      <canvas id="measurementCanvas" ref="measurementCanvas"></canvas>
      <!-- Help text overlay – stays fixed on the screen -->
      <div v-if="showLeuteinGuidelines" class="guidelines-overlay">
        <p>A: Canal orifice</p>
        <p>B: 2mm from A along coronal axis</p>
        <p>D: Apical foramen</p>
        <p>C: 1mm from D along apical axis</p>
      </div>
    </div>

    <!-- Confirm / Cancel buttons -->
    <div class="bottom-buttons">
      <button @click="confirmMeasurement" class="action-btn confirm-btn">
        <i class="fa-solid fa-check"></i>
        Confirm
      </button>
      <button @click="closeMeasurement" class="action-btn cancel-btn">
        <i class="fa-solid fa-xmark"></i>
        Cancel
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "MeasurementTool",
  data() {
    return {
      measurementEnabled: true,
      showLeuteinGuidelines: true,
      autoAdjustToLeutein: false,
      pixelsPerMm: 10,
      imageRotation: 0,
      backgroundImage: null,
      // Draggable measurement points.
      pointA: { x: 250, y: 150 },
      pointB: { x: 250, y: 170 },
      pointD: { x: 250, y: 350 },
      pointC: { x: 250, y: 340 },
      isDragging: false,
      draggedPoint: null, // "A", "B", "C", or "D"
      dragOffset: { x: 0, y: 0 },
      threshold: 15,
      canvasWidth: 500,
      canvasHeight: 500
    };
  },
  mounted() {
    this.canvas = this.$refs.measurementCanvas;
    this.ctx = this.canvas.getContext("2d");
    this.addEventListeners();
    
    this.$nextTick(() => {
      this.setupResponsiveCanvas();
      setTimeout(() => {
        this.setupResponsiveCanvas();
        this.draw();
      }, 50);
    });
    
    window.addEventListener('resize', this.setupResponsiveCanvas);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.setupResponsiveCanvas);
  },
  methods: {
    setupResponsiveCanvas() {
      const container = this.$refs.canvasContainer;
      const containerWidth = container.clientWidth;
      
      // Calculate available height by subtracting space for other elements
      const controlsHeight = this.$el.querySelector('.controls-row')?.offsetHeight || 80;
      const fileInputHeight = this.$el.querySelector('.file-input')?.offsetHeight || 60;
      const bottomButtonsHeight = this.$el.querySelector('.bottom-buttons')?.offsetHeight || 70;
      const padding = 40; // Account for margins and padding
      const angleDisplayHeight = 30; // Space for angle display
      
      const availableHeight = window.innerHeight - controlsHeight - fileInputHeight - bottomButtonsHeight - padding - angleDisplayHeight;
      
      this.canvasWidth = containerWidth;
      // Use the smaller of container width or available height, with a minimum
      this.canvasHeight = Math.max(250, Math.min(containerWidth, availableHeight));
      
      this.canvas.width = this.canvasWidth;
      this.canvas.height = this.canvasHeight;
      
      this.repositionPoints();
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
      this.draw();
    },
    repositionPoints() {
      const centerX = this.canvasWidth / 2;
      const topOffset = this.canvasHeight * 0.2;
      const bottomOffset = this.canvasHeight * 0.4;
      this.pointA = { x: centerX, y: topOffset };
      this.pointB = { x: centerX, y: topOffset + 20 };
      this.pointD = { x: centerX, y: this.canvasHeight - bottomOffset };
      this.pointC = { x: centerX, y: this.canvasHeight - bottomOffset - 10 };
    },
    addEventListeners() {
      this.canvas.addEventListener("mousedown", (e) => this.handlePointerDown(e));
      this.canvas.addEventListener("mousemove", (e) => this.handlePointerMove(e));
      this.canvas.addEventListener("mouseup", (e) => this.handlePointerUp(e));
      this.canvas.addEventListener("mouseleave", (e) => this.handlePointerUp(e));
      this.canvas.addEventListener("touchstart", (e) => this.handlePointerDown(e), { passive: false });
      this.canvas.addEventListener("touchmove", (e) => this.handlePointerMove(e), { passive: false });
      this.canvas.addEventListener("touchend", (e) => this.handlePointerUp(e), { passive: false });
      this.canvas.addEventListener("touchcancel", (e) => this.handlePointerUp(e), { passive: false });
    },
    getPointerPosition(e) {
      const rect = this.canvas.getBoundingClientRect();
      let clientX, clientY;
      if (e.touches && e.touches.length > 0) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }
      return { x: clientX - rect.left, y: clientY - rect.top };
    },
    drawPoint(x, y, label, color = "#3498db", radius = 8) {
      const responsiveRadius = Math.max(radius, this.canvasWidth * 0.016);
      const fontSize = Math.max(14, this.canvasWidth * 0.028);
      
      // Draw the circle
      this.ctx.beginPath();
      this.ctx.arc(x, y, responsiveRadius, 0, Math.PI * 2);
      this.ctx.fillStyle = color;
      this.ctx.fill();
      this.ctx.strokeStyle = "white";
      this.ctx.lineWidth = 2;
      this.ctx.stroke();
      
      // Add a small white background behind the text for better contrast
      const textWidth = this.ctx.measureText(label).width;
      this.ctx.fillStyle = "rgba(255, 255, 255, 0.8)";
      this.ctx.fillRect(
        x + responsiveRadius + 2, 
        y - responsiveRadius - fontSize, 
        textWidth + 4, 
        fontSize + 4
      );
      
      // Draw the text in the same color as the point
      this.ctx.font = `bold ${fontSize}px sans-serif`;
      this.ctx.fillStyle = color;
      this.ctx.fillText(label, x + responsiveRadius + 4, y - responsiveRadius - 4);
    },
    drawBackground() {
      if (this.backgroundImage) {
        try {
          this.ctx.save();
          this.ctx.translate(this.canvasWidth / 2, this.canvasHeight / 2);
          this.ctx.rotate(this.imageRotation);
          const maxWidth = this.canvasWidth * 0.8;
          const maxHeight = this.canvasHeight * 0.8;
          let width = this.backgroundImage.width;
          let height = this.backgroundImage.height;
          if (width > maxWidth || height > maxHeight || width === 0 || height === 0) {
            width = width || 1;
            height = height || 1;
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width *= ratio;
            height *= ratio;
          }
          if (this.backgroundImage.complete && this.backgroundImage.naturalWidth !== 0) {
            this.ctx.drawImage(
              this.backgroundImage,
              -width / 2,
              -height / 2,
              width,
              height
            );
          }
          this.ctx.restore();
        } catch (error) {
          console.error("Error drawing background image:", error);
          this.ctx.restore();
        }
      }
    },
    computeIntersection(p1, p2, p3, p4) {
      const a1 = p2.y - p1.y;
      const b1 = p1.x - p2.x;
      const c1 = a1 * p1.x + b1 * p1.y;
      const a2 = p4.y - p3.y;
      const b2 = p3.x - p4.x;
      const c2 = a2 * p3.x + b2 * p3.y;
      const determinant = a1 * b2 - a2 * b1;
      if (determinant === 0) {
        return null;
      } else {
        const x = (b2 * c1 - b1 * c2) / determinant;
        const y = (a1 * c2 - a2 * c1) / determinant;
        return { x, y };
      }
    },
    drawExtendedLine(p, q, color = "rgba(0, 0, 255, 0.4)") {
      const dx = q.x - p.x;
      const dy = q.y - p.y;
      if (dx === 0 && dy === 0) return;
      const t = 1000;
      const start = { x: p.x - dx * t, y: p.y - dy * t };
      const end = { x: p.x + dx * t, y: p.y + dy * t };
      this.ctx.beginPath();
      this.ctx.moveTo(start.x, start.y);
      this.ctx.lineTo(end.x, end.y);
      this.ctx.strokeStyle = color;
      this.ctx.lineWidth = 2;
      this.ctx.stroke();
    },
    drawAngleArc(intersection, v1, v2, angleDeg) {
      const mag1 = Math.hypot(v1.x, v1.y);
      const mag2 = Math.hypot(v2.x, v2.y);
      if (mag1 === 0 || mag2 === 0) return;
      const unit1 = { x: v1.x / mag1, y: v1.y / mag1 };
      const unit2 = { x: v2.x / mag2, y: v2.y / mag2 };
      const radius = Math.min(this.canvasWidth, this.canvasHeight) * 0.12;
      const startAngle = Math.atan2(unit1.y, unit1.x);
      const endAngle = Math.atan2(unit2.y, unit2.x);
      this.ctx.beginPath();
      const isClockwise = parseFloat(angleDeg) < 0;
      this.ctx.arc(
        intersection.x,
        intersection.y,
        radius,
        startAngle,
        endAngle,
        isClockwise
      );
      this.ctx.moveTo(intersection.x, intersection.y);
      this.ctx.lineTo(
        intersection.x + radius * Math.cos(startAngle),
        intersection.y + radius * Math.sin(startAngle)
      );
      this.ctx.moveTo(intersection.x, intersection.y);
      this.ctx.lineTo(
        intersection.x + radius * Math.cos(endAngle),
        intersection.y + radius * Math.sin(endAngle)
      );
      this.ctx.strokeStyle = "rgba(255, 165, 0, 0.8)";
      this.ctx.lineWidth = 3;
      this.ctx.stroke();
      this.ctx.beginPath();
      this.ctx.moveTo(intersection.x, intersection.y);
      this.ctx.arc(
        intersection.x,
        intersection.y,
        radius,
        startAngle,
        endAngle,
        isClockwise
      );
      this.ctx.closePath();
      this.ctx.fillStyle = "rgba(255, 165, 0, 0.2)";
      this.ctx.fill();
      const midAngle = (startAngle + endAngle) / 2;
      const labelRadius = radius * 0.7;
      const labelX = intersection.x + labelRadius * Math.cos(midAngle);
      const labelY = intersection.y + labelRadius * Math.sin(midAngle);
      this.ctx.font = "bold 16px sans-serif";
      this.ctx.fillStyle = "rgba(50, 50, 50, 0.8)";
      this.ctx.textAlign = "center";
      this.ctx.textBaseline = "middle";
      this.ctx.fillText(`${Math.abs(parseFloat(angleDeg)).toFixed(1)}°`, labelX, labelY);
    },
    draw() {
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
      this.drawBackground();

      // Draw the two axes:
      const intersection = this.computeIntersection(this.pointA, this.pointB, this.pointC, this.pointD);
      const v1 = { x: this.pointB.x - this.pointA.x, y: this.pointB.y - this.pointA.y };
      const v2 = { x: this.pointD.x - this.pointC.x, y: this.pointD.y - this.pointC.y };
      const angleDeg = this.calculateLeuteinAngle();
      const displayAngle = Math.abs(parseFloat(angleDeg)).toFixed(1);
      this.$refs.angleTopRight.textContent = `Curvature: ${displayAngle}°`;

      if (intersection) {
        this.drawExtendedLine(this.pointA, this.pointB, "rgba(0, 0, 255, 0.4)");
        this.drawExtendedLine(this.pointC, this.pointD, "rgba(255, 0, 0, 0.4)");
        this.drawAngleArc(intersection, v1, v2, angleDeg);
        this.drawPoint(intersection.x, intersection.y, "X", "#9b59b6", 6);
      } else {
        this.ctx.beginPath();
        this.ctx.moveTo(this.pointA.x, this.pointA.y);
        this.ctx.lineTo(this.pointB.x, this.pointB.y);
        this.ctx.strokeStyle = "rgba(0, 0, 255, 0.4)";
        this.ctx.lineWidth = 2;
        this.ctx.stroke();
        this.ctx.beginPath();
        this.ctx.moveTo(this.pointC.x, this.pointC.y);
        this.ctx.lineTo(this.pointD.x, this.pointD.y);
        this.ctx.strokeStyle = "rgba(255, 0, 0, 0.4)";
        this.ctx.lineWidth = 2;
        this.ctx.stroke();
      }

      // Draw draggable points.
      this.drawPoint(this.pointA.x, this.pointA.y, "A", "rgba(52, 152, 219, 0.3)"); // Translucent Bright blue
      this.drawPoint(this.pointB.x, this.pointB.y, "B", "rgba(41, 128, 185, 0.3)"); // Translucent Darker blue
      this.drawPoint(this.pointC.x, this.pointC.y, "C", "rgba(231, 76, 60, 0.3)"); // Translucent Bright red
      this.drawPoint(this.pointD.x, this.pointD.y, "D", "rgba(192, 57, 43, 0.3)"); // Translucent Darker red

      // Optionally, draw a faint central line.
      if (this.showLeuteinGuidelines) {
        this.drawCentralAxisLine();
      }
    },
    drawCentralAxisLine() {
      const centerX = this.canvasWidth / 2;
      this.ctx.beginPath();
      this.ctx.moveTo(centerX, 0);
      this.ctx.lineTo(centerX, this.canvasHeight);
      this.ctx.strokeStyle = "rgba(0, 0, 0, 0.1)";
      this.ctx.setLineDash([5, 5]);
      this.ctx.lineWidth = 1;
      this.ctx.stroke();
      this.ctx.setLineDash([]);
    },
    calculateLeuteinAngle() {
      const v1 = { x: this.pointB.x - this.pointA.x, y: this.pointB.y - this.pointA.y };
      const v2 = { x: this.pointD.x - this.pointC.x, y: this.pointD.y - this.pointC.y };
      const mag1 = Math.hypot(v1.x, v1.y);
      const mag2 = Math.hypot(v2.x, v2.y);
      let angleDeg = 0;
      if (mag1 && mag2) {
        const dot = v1.x * v2.x + v1.y * v2.y;
        const ratio = Math.max(-1, Math.min(1, dot / (mag1 * mag2)));
        const angleRad = Math.acos(ratio);
        angleDeg = (angleRad * 180 / Math.PI).toFixed(1);
        const cross = v1.x * v2.y - v1.y * v2.x;
        if (cross < 0) {
          angleDeg = -parseFloat(angleDeg);
        }
      }
      return angleDeg;
    },
    validateLeuteinPoints() {
      const verticalTolerance = this.canvasWidth * 0.3;
      const isVerticallyAligned = Math.abs(this.pointA.x - this.pointD.x) < verticalTolerance;
      const distanceAB = Math.hypot(this.pointB.x - this.pointA.x, this.pointB.y - this.pointA.y);
      const correctABDistance = 20;
      const isABDistanceCorrect = Math.abs(distanceAB - correctABDistance) < 5;
      const distanceCD = Math.hypot(this.pointC.x - this.pointD.x, this.pointC.y - this.pointD.y);
      const correctCDDistance = 10;
      const isCDDistanceCorrect = Math.abs(distanceCD - correctCDDistance) < 5;
      return {
        isValid: isVerticallyAligned && isABDistanceCorrect && isCDDistanceCorrect,
        issues: {
          verticalAlignment: !isVerticallyAligned,
          abDistance: !isABDistanceCorrect,
          cdDistance: !isCDDistanceCorrect
        }
      };
    },
    adjustPointsToLeuteinMethod() {
      // Adjust B relative to A.
      const vAB = { 
        x: this.pointB.x - this.pointA.x, 
        y: this.pointB.y - this.pointA.y 
      };
      const magAB = Math.hypot(vAB.x, vAB.y);
      if (magAB > 0) {
        this.pointB.x = this.pointA.x + (vAB.x / magAB) * 20;
        this.pointB.y = this.pointA.y + (vAB.y / magAB) * 20;
      } else {
        this.pointB.x = this.pointA.x;
        this.pointB.y = this.pointA.y + 20;
      }
      // Adjust C relative to D.
      const vDC = { 
        x: this.pointC.x - this.pointD.x, 
        y: this.pointC.y - this.pointD.y 
      };
      const magDC = Math.hypot(vDC.x, vDC.y);
      if (magDC > 0) {
        this.pointC.x = this.pointD.x + (vDC.x / magDC) * 10;
        this.pointC.y = this.pointD.y + (vDC.y / magDC) * 10;
      } else {
        this.pointC.x = this.pointD.x;
        this.pointC.y = this.pointD.y - 10;
      }
      this.draw();
    },
    handlePointerDown(e) {
      e.preventDefault();
      const pos = this.getPointerPosition(e);
      const distances = {
        A: Math.hypot(pos.x - this.pointA.x, pos.y - this.pointA.y),
        B: Math.hypot(pos.x - this.pointB.x, pos.y - this.pointB.y),
        C: Math.hypot(pos.x - this.pointC.x, pos.y - this.pointC.y),
        D: Math.hypot(pos.x - this.pointD.x, pos.y - this.pointD.y)
      };
      const minDistance = Math.min(distances.A, distances.B, distances.C, distances.D);
      if (minDistance < this.threshold) {
        if (minDistance === distances.A) {
          this.draggedPoint = "A";
          this.initialA = { ...this.pointA };
        } else if (minDistance === distances.B) {
          this.draggedPoint = "B";
        } else if (minDistance === distances.C) {
          this.draggedPoint = "C";
        } else if (minDistance === distances.D) {
          this.draggedPoint = "D";
          this.initialD = { ...this.pointD };
        }
        this.dragOffset.x = (this.draggedPoint === "A"
          ? this.pointA.x
          : this.draggedPoint === "B"
          ? this.pointB.x
          : this.draggedPoint === "C"
          ? this.pointC.x
          : this.pointD.x) - pos.x;
        this.dragOffset.y = (this.draggedPoint === "A"
          ? this.pointA.y
          : this.draggedPoint === "B"
          ? this.pointB.y
          : this.draggedPoint === "C"
          ? this.pointC.y
          : this.pointD.y) - pos.y;
      }
      this.draw();
    },
    handlePointerMove(e) {
      if (!this.isDragging && !this.draggedPoint) return;
      e.preventDefault();
      const pos = this.getPointerPosition(e);
      // For A and D, move them independently.
      if (this.draggedPoint === "A") {
        this.pointA.x = pos.x + this.dragOffset.x;
        this.pointA.y = pos.y + this.dragOffset.y;
      } else if (this.draggedPoint === "D") {
        this.pointD.x = pos.x + this.dragOffset.x;
        this.pointD.y = pos.y + this.dragOffset.y;
      } else if (this.draggedPoint === "B") {
        this.pointB.x = pos.x + this.dragOffset.x;
        this.pointB.y = pos.y + this.dragOffset.y;
        if (this.autoAdjustToLeutein) {
          const vAB = { 
            x: this.pointB.x - this.pointA.x, 
            y: this.pointB.y - this.pointA.y 
          };
          const magAB = Math.hypot(vAB.x, vAB.y);
          if (magAB > 0) {
            this.pointB.x = this.pointA.x + (vAB.x / magAB) * 20;
            this.pointB.y = this.pointA.y + (vAB.y / magAB) * 20;
          }
        }
      } else if (this.draggedPoint === "C") {
        this.pointC.x = pos.x + this.dragOffset.x;
        this.pointC.y = pos.y + this.dragOffset.y;
        if (this.autoAdjustToLeutein) {
          const vDC = { 
            x: this.pointC.x - this.pointD.x, 
            y: this.pointC.y - this.pointD.y 
          };
          const magDC = Math.hypot(vDC.x, vDC.y);
          if (magDC > 0) {
            this.pointC.x = this.pointD.x + (vDC.x / magDC) * 10;
            this.pointC.y = this.pointD.y + (vDC.y / magDC) * 10;
          }
        }
      }
      this.draw();
    },
    handlePointerUp(e) {
      this.isDragging = false;
      this.draggedPoint = null;
      this.draw();
    },
    rotateCW() {
      this.imageRotation += Math.PI / 2;
      this.draw();
    },
    rotateCCW() {
      this.imageRotation -= Math.PI / 2;
      this.draw();
    },
    resetTool() {
      this.imageRotation = 0;
      this.repositionPoints();
      this.draw();
    },
    handleImageUpload(e) {
      const file = e.target.files[0];
      if (!file) return;

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        this.toast.error('Please select only JPG, JPEG, or PNG images');
        event.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          this.originalImageBase64 = event.target.result; 
          this.backgroundImage = img;
          this.imageRotation = 0;
          this.repositionPoints();
          this.$nextTick(() => {
            this.setupResponsiveCanvas();
            setTimeout(() => {
              this.setupResponsiveCanvas();
              this.draw();
            }, 50);
          });
        };
        img.crossOrigin = "Anonymous";
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    },
    confirmMeasurement() {
      const annotatedImageBase64 = this.canvas.toDataURL();
      const rawAngle = this.calculateLeuteinAngle();
      const angle = Math.abs(parseFloat(rawAngle)).toFixed(1);
      const direction = parseFloat(rawAngle) < 0 ? 'right' : 'left';

      this.$emit("measurement-confirmed", {
        annotatedImageBase64,
        originalImageBase64: this.originalImageBase64,
        angle,
        direction
      });
    },
    closeMeasurement() {
      this.$emit("measurement-closed");
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../breakpoints.scss";

.measurement-tool {
  margin: 0 auto;
  padding: 10px;
  background-color: #fdfdfd;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  max-width: 100%;
  overflow: hidden;
  
  // Ensure the component fits within viewport
  max-height: 100vh;
  display: flex;
  flex-direction: column;
  
  @include md {
    padding: 15px;
  }
}

.controls-row {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 15px;
  flex-shrink: 0; // Prevent shrinking
  
  @include sm {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
  }
  
  @include md {
    flex-direction: row;
    align-items: center;
  }
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  
  @include sm {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 15px;
  }
}

.checkbox-group {
  display: flex;
  align-items: center;
  gap: 8px;
  
  label {
    font-size: 14px;
    
    @include md {
      font-size: 16px;
    }
  }
}

.button-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 8px;
  
  @include sm {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @include md {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    margin-top: 0;
  }
}

.control-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px;
  border: 1px solid #ccc;
  background-color: #f2f2f2;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #e0e0e0;
  }
  
  i {
    font-size: 16px;
  }
  
  .btn-text {
    display: none;
    
    @include md {
      display: block;
    }
  }
  
  @include md {
    flex-direction: row;
    font-size: 14px;
    padding: 8px 12px;
  }
}

.file-input {
  margin-bottom: 15px;
  text-align: center;
  flex-shrink: 0; // Prevent shrinking
  
  input[type="file"] {
    display: none;
  }
  
  .file-upload-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background-color: #013B82;
    color: white;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: darken(#013B82, 10%);
    }
  }
}

#angle-bottom-right {
  position: absolute;
  bottom: 80px;
  right: 20px;
  font-size: 16px;
  background-color: rgba(255, 255, 255, 0.7);
  padding: 5px 10px;
  border-radius: 5px;
  z-index: 10;
  
  @include md {
    font-size: 18px;
  }
}

.canvas-container {
  width: 100%;
  position: relative;
  margin: 0 auto;
  border: 1px solid #ddd;
  overflow: hidden;
  flex: 1; // Take up remaining space
  min-height: 250px; // Ensure minimum height
  
  canvas {
    display: block;
    background-color: #f8f8f8;
    width: 100%;
    height: auto;
    touch-action: none;
  }
  
  .guidelines-overlay {
    position: absolute;
    top: 15px;
    left: 15px;
    background: rgba(255, 255, 255, 0.9);
    padding: 10px;
    border: 1px solid rgba(0, 0, 0, 0.3);
    border-radius: 4px;
    font-size: 14px;
    line-height: 1.4;
    z-index: 20;
  }
}

.bottom-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 15px;
  flex-shrink: 0; // Prevent shrinking
  
  @include md {
    gap: 20px;
    margin-top: 20px;
  }
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 100px;
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  @include md {
    min-width: 120px;
  }
}

.confirm-btn {
  background-color: #4CAF50;
  color: white;
  
  &:hover {
    background-color: darken(#4CAF50, 10%);
  }
}

.cancel-btn {
  background-color: #f44336;
  color: white;
  
  &:hover {
    background-color: darken(#f44336, 10%);
  }
}
</style>